import { useState } from "owa-react";
import React from "react";
import classNames from "owa-classnames";
import { Virtuoso } from "react-virtuoso";
import type { VirtuosoHandle, ListRange } from "react-virtuoso";
import { observer } from "owa-mobx-react";
import type { VirtuosoLoadMoreListViewProps, VirtuosoLoadMoreListViewRef, } from "./LoadMoreListView.types";
import { rowWrapper, listAutoSizer, row, minHeightRow } from "./VirtuosoLoadMoreListView.scss";
import { isCurrentCultureRightToLeft } from "owa-localize";
import { safeRequestAnimationFrame, wrapForcedLayout } from "owa-performance";
import { getListViewState } from "owa-mail-list-store";
import onMailListLoaded from "owa-mail-logging/lib/utils/mailListLoad/onMailListLoaded";
import { getReadyMailListLoadId, isMailListLoadGenerationEnabled, subscribeToMailListLoadChanges, } from "owa-mail-logging/lib/utils/mailListLoad/mailListLoadDatapointGetterAndSetter";
import { isGlobalFeatureEnabled } from "owa-feature-flags";

// PAGINATION_PREFETCH_BUFFER defines the size of the preload pagination buffer
// height as a factor of the viewport height. For example, if the viewport height
// is 1000px and the PAGINATION_PREFETCH_BUFFER is 2, then the preload buffer
// height will be 2000px. When the user scrolls within 2000px of the bottom
// of the list, props.onLoadMore will be called to begin prefetching the next
// "page" of rows. Akin to LoadMoreListView's DEFAULT_GUARD_PAGE_COUNT.
const PAGINATION_PREFETCH_BUFFER = 2;

const defaultExport: React.MemoExoticComponent<React.ForwardRefExoticComponent<Omit<VirtuosoLoadMoreListViewProps<any, any> & React.RefAttributes<VirtuosoLoadMoreListViewRef>, "ref"> & React.RefAttributes<VirtuosoLoadMoreListViewRef>>> = observer(React.forwardRef<VirtuosoLoadMoreListViewRef, VirtuosoLoadMoreListViewProps<any, any> // this shouldn't be 'any' but couldn't figure out how to do the types
>(function VirtuosoLoadMoreListView<TListProps, TSubComponentProps>(props: VirtuosoLoadMoreListViewProps<TListProps, TSubComponentProps>, ref: React.Ref<VirtuosoLoadMoreListViewRef>) {
    const { className, currentLoadedIndex, estimatedRowHeight, getCanLoadMore, getCanLoadMorePrevious, isLoadRowsInProgress, onLoadMoreRows, onScroll, focusedRowKeyIndex, focusedNodeId, activeAnimationsCount, itemIds, listProps, loadingComponent, onRenderRow, onRenderHeader, PreRowsComponent, MidRowsComponent, midRowsComponentRowNumber, PostRowsComponent, subComponentProps, hiddenRowIndices, focusedRowKey, updateStartAndEndIndices, loadedStartIndex, } = props;
    
    const virtuosoRef = React.useRef<VirtuosoHandle>(null);
    const scrollingRegionRef = React.useRef<HTMLElement | Window | null>(null);
    const skipScrollToFocusedRow = React.useRef<boolean>(false);
    const prevExpandedRowKeyRef = React.useRef<string | null>(null);
    const pendingCollapseScrollCheckRef = React.useRef<boolean>(false);
    const pendingFocusedRowScrollRef = React.useRef<boolean>(false);
    const itemIdsRef = React.useRef(itemIds);
    
    // Previous-value refs used by the row-insertion useLayoutEffect below.
    const prevFocusedRowKeyRef = React.useRef(focusedRowKey);
    const prevFocusedRowKeyIndexRef = React.useRef(focusedRowKeyIndex);
    const prevLoadedStartIndexRef = React.useRef(loadedStartIndex);
    React.useEffect(() => {
        itemIdsRef.current = itemIds;
    }, [itemIds]);
    const [isScrollingValue, setIsScrollingValue] = useState(false);
    const isMailListLoadGenerationFlightEnabled = isMailListLoadGenerationEnabled();
    const mailListLoadId = React.useSyncExternalStore(subscribeToMailListLoadChanges, () => isMailListLoadGenerationFlightEnabled
        ? getReadyMailListLoadId(props.dataSourceId)
        : undefined, () => undefined);
    
    React.useEffect(() => {
        if (!isMailListLoadGenerationFlightEnabled || mailListLoadId === undefined) {
            return;
        }
        
        safeRequestAnimationFrame(() => {
            onMailListLoaded("VML" /*componentRendered*/, props.dataSourceId /*tableViewId*/, mailListLoadId);
        });
    }, [props.dataSourceId, isMailListLoadGenerationFlightEnabled, mailListLoadId]);
    
    React.useEffect(() => {
        // If the focusedRowKeyIndex is > 0, we want to allow the scroll to focused row logic to run.
        // Otherwise, if focusedRowKeyIndex is undefined or 0, we want to skip the scroll to focused
        // row logic and just scroll to the top of the list.
        if (focusedRowKeyIndex && focusedRowKeyIndex > 0) {
            skipScrollToFocusedRow.current = false;
            if (!isMailListLoadGenerationFlightEnabled) {
                safeRequestAnimationFrame(() => {
                    onMailListLoaded("VML" /*componentRendered*/, props.dataSourceId /*tableViewId*/);
                });
            }
            return;
        }
        
        // On page load and folder switches, skip the scroll to focused row logic because we want to stay scrolled
        // at the top of the list for the PreRowsComponent (ads list) and pinned items.
        skipScrollToFocusedRow.current = true;
        safeRequestAnimationFrame(() => {
            if (!isMailListLoadGenerationFlightEnabled) {
                onMailListLoaded("VML" /*componentRendered*/, props.dataSourceId /*tableViewId*/);
            }
            const scrollingRegionRefVal = scrollingRegionRef.current;
            wrapForcedLayout("VirtuosoLoadMoreListView_scrollTop", () => {
                if (scrollingRegionRefVal) {
                    /* eslint-disable-next-line no-restricted-properties -- (https://aka.ms/OWALintWiki)
                     Justification: On page load and folder switches, we want to scroll to the top of the list.
                     This is done by setting the scrollTop of the scrolling region to 0 */
                    (scrollingRegionRefVal as HTMLDivElement).scrollTop = 0;
                }
            });
            skipScrollToFocusedRow.current = false; // reset back to false after the scrollTop has been set.
        });
    }, [props.dataSourceId, isMailListLoadGenerationFlightEnabled]);
    React.useImperativeHandle(ref, () => ({
        getScrollRegion: () => scrollingRegionRef.current as HTMLDivElement,
        setFocus: () => scrollingRegionRef.current?.focus(),
        scrollToIndex: (numPreviousRowsLoaded: number, _firstNewRowInstanceKey: string | undefined) => {
            // On load previous calls, we want to scroll to wherever the list was previously scrolled to after we have loaded
            // more rows above. That's why we add startIndex to numPreviousRowsLoaded. startIndex is the index of the row that
            // was previously scrolled to before the load more rows call.
            const startIndex = listVisibleRange.current.startIndex;
            const targetIndex = numPreviousRowsLoaded + startIndex;
            // Defer the scroll by a frame so Virtuoso has a chance to render
            // and measure the newly-prepended rows before computing the
            // pixel offset for targetIndex. Without this, Virtuoso uses
            // defaultItemHeight (estimatedRowHeight) for the unmeasured
            // prepended rows.
            safeRequestAnimationFrame(() => {
                virtuosoRef.current?.scrollToIndex({
                    index: targetIndex,
                    align: "start",
                    behavior: "auto",
                });
            });
        },
    }), [props.itemIds, focusedRowKey, focusedRowKeyIndex]);
    
    const isRtl = isCurrentCultureRightToLeft();
    const listStyle: React.CSSProperties = React.useMemo(() => {
        return isRtl
            ? {
                direction: "rtl",
            }
            : {};
    }, [isRtl]);
    
    const listVisibleRange = React.useRef<ListRange>({
        startIndex: 0,
        endIndex: 0,
    });
    
    // When rows are un-hidden (e.g. a group header is expanded), the newly-revealed
    // rows may have indices beyond listVisibleRange.current.endIndex because that ref
    // is stale (only updated by onRangeChanged after Virtuoso re-measures).  Without
    // this, those rows render as 1px placeholders and never get a re-render to full
    // quality — especially in views where load-more is unsupported.
    //
    // We use a ref flag that stays true from the moment rows are unhidden until
    // onRangeChanged fires (meaning Virtuoso has re-measured and the visible range
    // is accurate). This survives multiple render passes that Virtuoso may trigger
    // during measurement cascades. Reading a ref in renderRow avoids stale closures.
    const prevHiddenRowCountRef = React.useRef(hiddenRowIndices.length);
    const unhideInProgressRef = React.useRef(false);
    if (hiddenRowIndices.length < prevHiddenRowCountRef.current) {
        unhideInProgressRef.current = true;
    }
    prevHiddenRowCountRef.current = hiddenRowIndices.length;
    React.useEffect(() => {
        prevHiddenRowCountRef.current = hiddenRowIndices.length;
        unhideInProgressRef.current = false;
    }, [props.dataSourceId]);
    
    // Suppress auto-load-previous when loadedStartIndex jumps to a non-zero
    // position (End-key load, JumpTo End fallback, JumpTo into the middle).
    // Set during render so it lands before Virtuoso's child effects can fire
    // onRangeChanged. Cleared in onRangeChanged once startIndex > 0 or the
    // focused row enters the visible range. prevLoadedStartIndexForSuppressRef
    // starts at 0 because MailListContent unmounts/remounts during JumpTo,
    // and initializing to the post-load value would mask the delta.
    const suppressLoadPreviousRef = React.useRef<boolean>(false);
    const prevLoadedStartIndexForSuppressRef = React.useRef<number>(0);
    if (loadedStartIndex > prevLoadedStartIndexForSuppressRef.current) {
        suppressLoadPreviousRef.current = true;
    }
    prevLoadedStartIndexForSuppressRef.current = loadedStartIndex;
    
    // Coalesce updateStartAndEndIndices into a single rAF-deferred write so that the mutator
    // (and the MobX-driven React re-renders it triggers) runs OUTSIDE the ResizeObserver delivery task that
    // produced this range change. Calling the mutator synchronously here extends the
    // current Long Animation Frame because RO callbacks all fire in one task right
    // before paint, and any React commits they cause stay on the same task and can
    // trigger another synchronous layout pass.
    const pendingRangeRef = React.useRef<ListRange | null>(null);
    const flushScheduledRef = React.useRef<boolean>(false);
    const isMountedRef = React.useRef<boolean>(true);
    React.useEffect(() => () => {
        isMountedRef.current = false;
    }, []);
    const flushPendingRange = React.useCallback(() => {
        flushScheduledRef.current = false;
        const pending = pendingRangeRef.current;
        pendingRangeRef.current = null;
        if (pending && isMountedRef.current) {
            updateStartAndEndIndices(pending.startIndex, pending.endIndex);
        }
    }, [updateStartAndEndIndices]);
    
    const onRangeChanged = React.useCallback((range: ListRange) => {
        const { startIndex, endIndex } = range;
        listVisibleRange.current = range;
        unhideInProgressRef.current = false;
        
        if (isGlobalFeatureEnabled("tri-vlv-range-update-coalesce")) {
            pendingRangeRef.current = range;
            if (!flushScheduledRef.current) {
                flushScheduledRef.current = true;
                safeRequestAnimationFrame(flushPendingRange);
            }
        }
        else {
            updateStartAndEndIndices(startIndex, endIndex);
        }
        
        const isFocusedRowInVisibleRange = focusedRowKeyIndex !== null &&
            focusedRowKeyIndex !== undefined &&
            focusedRowKeyIndex !== -1 &&
            focusedRowKeyIndex >= startIndex &&
            focusedRowKeyIndex <= endIndex;
        if (startIndex > 0 || isFocusedRowInVisibleRange) {
            suppressLoadPreviousRef.current = false;
        }
        
        // Check if we have a pending scroll check from a conversation collapse.
        // This needs to happen in onRangeChanged because the visible range is now accurate
        // after the DOM has updated following the collapse.
        if (pendingCollapseScrollCheckRef.current &&
            focusedRowKeyIndex !== null &&
            focusedRowKeyIndex !== undefined &&
            focusedRowKeyIndex !== -1) {
            pendingCollapseScrollCheckRef.current = false;
            const isRowAboveStartIndex = focusedRowKeyIndex < startIndex;
            if (isRowAboveStartIndex) {
                virtuosoRef.current?.scrollToIndex({
                    index: focusedRowKeyIndex,
                    align: "start",
                });
            }
        }
        
        // Check if we have a pending scroll to the focused row deferred from the
        // scroll-to-focused-row useEffect. This happens when the Virtuoso component
        // remounts (e.g. after exiting search) and the visible range was still {0,0}
        // when the effect ran, so the scroll was deferred until a real range is reported.
        // Bail out when the range is still {0,0} — Virtuoso can emit that before a
        // real range, so we keep the deferral active until an accurate range arrives.
        // Also respect skipScrollToFocusedRow to avoid scrolling during folder
        // switches / page-loads where the flag is set but the rAF that clears it
        // hasn't executed yet.
        if (pendingFocusedRowScrollRef.current) {
            if (skipScrollToFocusedRow.current) {
                pendingFocusedRowScrollRef.current = false;
            }
            else if (focusedRowKeyIndex !== null &&
                focusedRowKeyIndex !== undefined &&
                focusedRowKeyIndex !== -1 &&
                !(startIndex === 0 && endIndex === 0)) {
                pendingFocusedRowScrollRef.current = false;
                const isAbove = focusedRowKeyIndex < startIndex;
                const isBelow = focusedRowKeyIndex > endIndex;
                if (isAbove || isBelow) {
                    virtuosoRef.current?.scrollToIndex({
                        index: focusedRowKeyIndex,
                        align: "start",
                    });
                }
            }
        }
        
        // Calculate the number of hidden rows in the visible range.
        let numHiddenRows = 0;
        for (const index of hiddenRowIndices) {
            if (index + 1 >= startIndex && index + 1 <= endIndex) {
                numHiddenRows++;
            }
        }
        
        // If the end of the fetched list is visible and we can load more rows below, we should prioritize loading more rows in the next page.
        const areAllItemsAtBottomVisibleAndCanFetchNext = currentLoadedIndex - 1 === endIndex && getCanLoadMore();
        
        const bufferedElements = Math.max((endIndex - startIndex - numHiddenRows) * PAGINATION_PREFETCH_BUFFER, 0);
        const nearVisibleEnd = currentLoadedIndex - endIndex < bufferedElements;
        const nearVisibleStart = startIndex === 0;
        
        if (!suppressLoadPreviousRef.current &&
            !areAllItemsAtBottomVisibleAndCanFetchNext &&
            nearVisibleStart &&
            getCanLoadMorePrevious() &&
            activeAnimationsCount === 0) {
            onLoadMoreRows("PreviousPage");
        }
        
        if (nearVisibleEnd && getCanLoadMore() && activeAnimationsCount === 0) {
            onLoadMoreRows("NextPage");
        }
    }, [
        flushPendingRange,
        updateStartAndEndIndices,
        currentLoadedIndex,
        getCanLoadMore,
        getCanLoadMorePrevious,
        onLoadMoreRows,
        activeAnimationsCount,
        hiddenRowIndices,
        focusedRowKeyIndex,
    ]);
    
    const renderHeaderAboveRow = React.useCallback((rowToRender: number): React.JSX.Element | null => {
        if (onRenderHeader) {
            const previousElementId = rowToRender > 0 && rowToRender <= currentLoadedIndex
                ? itemIds[rowToRender - 1]
                : null;
            const currentElementId = itemIds[rowToRender];
            return onRenderHeader(previousElementId, currentElementId);
        }
        return null;
    }, [onRenderHeader, currentLoadedIndex, itemIds]);
    
    const rowStyle: React.CSSProperties = React.useMemo(() => {
        return isScrollingValue
            ? {
                pointerEvents: "none",
            }
            : {};
    }, [isScrollingValue]);
    
    const renderRow = React.useCallback((index: number, rowKey: string, contextListProps?: TListProps): React.JSX.Element | null => {
        if (index < 0 || index >= itemIds.length) {
            return null;
        }
        
        const isRowInCollapsedGroup = hiddenRowIndices.includes(index);
        
        const isNotVisible = isRowInCollapsedGroup ||
            (!unhideInProgressRef.current &&
                (index < listVisibleRange.current.startIndex ||
                    index > listVisibleRange.current.endIndex));
        
        const rowToRender = onRenderRow(rowKey, index, contextListProps ?? listProps, 
        // render minimum if we are scrolling or the row is not visible
        isScrollingValue || isNotVisible);
        const header = renderHeaderAboveRow(index);
        const midRowsComponent = index === midRowsComponentRowNumber && MidRowsComponent ? (<MidRowsComponent {...(subComponentProps as any)}/>) : null;
        
        const loadingSpinnerComponent = index === itemIds.length - 1 && isLoadRowsInProgress && loadingComponent;
        
        if (header ||
            midRowsComponent ||
            rowToRender /* used to be null, now <></> */ ||
            loadingSpinnerComponent) {
            return (<div data-animatable={true} className={classNames(row, !isRowInCollapsedGroup && minHeightRow)} style={rowStyle}>
                            {header}
                            {midRowsComponent}
                            {rowToRender}
                            {/* The last row gets a loading spinner if we're actively loading more data */}
                            {loadingSpinnerComponent}
                        </div>);
        }
        else {
            return !isRowInCollapsedGroup ? <div className={minHeightRow}></div> : null;
        }
    }, [
        isLoadRowsInProgress,
        loadingComponent,
        itemIds,
        onRenderRow,
        renderHeaderAboveRow,
        listProps,
        midRowsComponentRowNumber,
        MidRowsComponent,
        isScrollingValue,
        hiddenRowIndices,
    ]);
    
    // When rows are inserted above the focused row its index increases while the key stays
    // the same.  The existing useEffect below fires only after paint, by which point Virtuoso
    // may have already removed the row from its virtual window (triggering focus-rescue and
    // screen-reader announcements in MailListItemContainer).  This useLayoutEffect fires
    // synchronously before paint so the scroll reaches Virtuoso before it decides the row
    // is outside its render buffer.
    React.useLayoutEffect(() => {
        const prevKey = prevFocusedRowKeyRef.current;
        const prevIndex = prevFocusedRowKeyIndexRef.current;
        const prevLoadedStartIndex = prevLoadedStartIndexRef.current;
        prevFocusedRowKeyRef.current = focusedRowKey;
        prevFocusedRowKeyIndexRef.current = focusedRowKeyIndex;
        prevLoadedStartIndexRef.current = loadedStartIndex;
        
        if (skipScrollToFocusedRow.current ||
            !focusedRowKey ||
            focusedRowKeyIndex == null ||
            focusedRowKeyIndex === -1 ||
            // Key changed → user navigated to a different row; let the useEffect below handle it.
            focusedRowKey !== prevKey ||
            prevIndex == null ||
            prevIndex === -1 ||
            // Index did not increase → no rows were inserted above this row.
            focusedRowKeyIndex <= prevIndex ||
            // loadedStartIndex changed → this is a "load previous page" window shift
            // (e.g. Jump To scenario) whose scroll is already managed by scrollToIndex
            // on the ref; don't interfere.
            loadedStartIndex !== prevLoadedStartIndex) {
            return;
        }
        
        // listVisibleRange is updated by onRangeChanged, which fires from scroll events —
        // so at this point it reflects the viewport state before the insertion.
        // Only scroll if prevIndex was actually visible; if the user had already scrolled
        // the focused row out of view we should not steal their scroll position.
        const { startIndex, endIndex } = listVisibleRange.current;
        const prevIndexWasInView = prevIndex >= startIndex && prevIndex <= endIndex;
        if (focusedRowKeyIndex > endIndex && prevIndexWasInView) {
            virtuosoRef.current?.scrollToIndex({
                index: focusedRowKeyIndex,
                align: "end",
                behavior: "auto",
            });
        }
    }, [focusedRowKey, focusedRowKeyIndex, loadedStartIndex]);
    
    // When the tableView's focusedRowKey changes, we want to trigger this useEffect to perform a calculation
    // to determine whether the new focusedRowKey is in view (between the visible indices).
    // If it is not within the current visible indices, we want to scroll to that item.
    const expandedRowKey = getListViewState().expandedConversationViewState.expandedRowKey;
    const { jumpToRowKey, jumpToRequestId } = getListViewState();
    // Starts at the current request so a remounted list doesn't replay an earlier Jump To.
    const handledJumpToRequestIdRef = React.useRef(jumpToRequestId);
    React.useEffect(() => {
        const startIndex = listVisibleRange.current.startIndex;
        const endIndex = listVisibleRange.current.endIndex;
        
        // Jump To focused a row that was already loaded: bring it to the top of the list, the
        // same place Jump To lands when it reloads the table, even if it is already in view.
        const isJumpToRow = jumpToRequestId !== handledJumpToRequestIdRef.current &&
            jumpToRowKey === focusedRowKey;
        handledJumpToRequestIdRef.current = jumpToRequestId;
        
        // Check if a row just collapsed. We execute some logic below to determine if we need to scroll to the focused row
        // after a collapse because a collapse can shift rows up and cause the focused row to be out of view
        const prevExpandedRowKey = prevExpandedRowKeyRef.current;
        const didRowCollapse = !!prevExpandedRowKey && prevExpandedRowKey !== expandedRowKey;
        const collapsedRowIndex = prevExpandedRowKey
            ? itemIdsRef.current.indexOf(prevExpandedRowKey)
            : -1;
        const isCollapsedRowAtFirstVisibleIndexOrAbove = collapsedRowIndex !== -1 && collapsedRowIndex <= startIndex;
        prevExpandedRowKeyRef.current = expandedRowKey;
        
        if (focusedRowKeyIndex !== null &&
            focusedRowKeyIndex !== undefined &&
            focusedRowKeyIndex !== -1 &&
            !skipScrollToFocusedRow.current // If skipScrollToFocusedRow is true, we don't want to scroll to the focused row.
        ) {
            // If the start and end indices are both 0, the Virtuoso component either just
            // remounted (e.g. after exiting search) or hasn't reported its range yet.
            // Defer the scroll to onRangeChanged where the range will be accurate.
            if (startIndex === 0 && endIndex === 0) {
                pendingFocusedRowScrollRef.current = true;
                return;
            }
            
            const indexToScrollTo = focusedRowKeyIndex;
            const isRowAboveStartIndex = indexToScrollTo < startIndex;
            const isRowBelowEndIndex = indexToScrollTo > endIndex;
            if (isJumpToRow) {
                virtuosoRef.current?.scrollToIndex({
                    index: indexToScrollTo,
                    align: "start",
                });
            }
            else if (isRowAboveStartIndex || isRowBelowEndIndex) {
                virtuosoRef.current?.scrollToIndex({
                    index: indexToScrollTo,
                    align: isRowAboveStartIndex ? "start" : "end",
                });
            }
            else if (didRowCollapse && isCollapsedRowAtFirstVisibleIndexOrAbove) {
                // Row appears to be in view, but a collapse just happened at the first visible index or above.
                // This means rows get shifted upward and the focused row may end up being out of view (above the viewport).
                // Set a flag to check the new focused row's visibility in onRangeChanged after the DOM has updated,
                // so we can accurately determine if the focused row is still visible.
                pendingCollapseScrollCheckRef.current = true;
            }
        }
        else {
            // focusedRowKeyIndex is invalid or scroll is being skipped — clear any
            // pending deferred scroll so it doesn't fire unexpectedly later (e.g.
            // after a tab/view switch while the range was still {0,0}).
            pendingFocusedRowScrollRef.current = false;
        }
    }, [focusedRowKey, focusedNodeId, props.dataSourceId, expandedRowKey, jumpToRequestId]);
    
    const handleScrollerRef = React.useCallback((scrollRef: HTMLElement | Window | null) => {
        scrollingRegionRef.current = scrollRef;
    }, []);
    
    const onListScroll = React.useCallback((isScrolling: boolean) => {
        setIsScrollingValue(isScrolling);
        const scrollingRegion = scrollingRegionRef.current as HTMLDivElement;
        if (isScrolling && scrollingRegion && onScroll) {
            onScroll(scrollingRegion);
        }
    }, [onScroll]);
    
    const virtuosoComponents = React.useMemo(() => ({
        Header: () => PreRowsComponent ? <PreRowsComponent {...(subComponentProps as any)}/> : null,
        Footer: () => PostRowsComponent ? (<PostRowsComponent {...(subComponentProps as any)}/>) : null,
    }), [PreRowsComponent, PostRowsComponent, subComponentProps]);
    
    return (<div className={classNames(rowWrapper, props.rowWrapperClass)}>
                <div className={listAutoSizer}>
                    <Virtuoso key={props.dataSourceId} components={virtuosoComponents} className={className} scrollerRef={handleScrollerRef} ref={virtuosoRef} style={listStyle} data={itemIds} itemContent={renderRow} context={listProps} rangeChanged={onRangeChanged} totalCount={itemIds.length} defaultItemHeight={estimatedRowHeight} isScrolling={onListScroll} skipAnimationFrameInResizeObserver={true} computeItemKey={computeItemKey} tabIndex={-1}/>
                </div>
            </div>);
}), "VirtuosoLoadMoreListView");
export default defaultExport;

function computeItemKey(_: number, itemId: string) {
    return itemId;
}
